<template>
  <div class="rights-page">
    <header class="rights-page__header">
      <div>
        <h1 class="page__title">{{ $t('settingsPage.userRights') }}</h1>
        <p>{{ $t('userRightsPage.intro') }}</p>
      </div>
      <div class="rights-page__actions">
        <button v-if="can('role_create')" type="button" class="button button--default" @click="store.commit('setShowAddRoleModal', true)">{{ $t('addRoleModal.addRoleBtn') }}</button>
        <button v-if="can('permission_create')" type="button" class="rights-page__secondary button button--cancel" @click="store.commit('setShowAddPermissionModal', true)">{{ $t('addPermissionModal.title') }}</button>
      </div>
    </header>

    <div class="rights-page__team box box--default">
      <label for="rights-team">{{ $t('settingsPage.team') }}</label>
      <select id="rights-team" class="input input--default" :value="activeTeamId || ''" :disabled="teamsLoading || rolesLoading || saving" @change="onTeamChange">
        <option value="" disabled>{{ $t('addRoleModal.selectTeam') }}</option>
        <option v-for="team in roleTeams" :key="team.id" :value="team.id">{{ team.name }}</option>
      </select>
    </div>

    <div v-if="teamsLoading" class="rights-page__state box box--default" role="status">{{ $t('userRightsPage.loadingTeams') }}</div>
    <div v-else-if="!roleTeams.length" class="rights-page__state box box--default">{{ $t('userRightsPage.noTeams') }}</div>
    <div v-else class="rights-page__layout">
      <aside class="rights-page__roles box box--default" aria-labelledby="rights-roles-heading">
        <div class="rights-page__heading">
          <h2 id="rights-roles-heading">{{ $t('userRightsPage.roles') }}</h2>
          <span v-if="!rolesLoading && !roleCollectionError">{{ roles.length }}</span>
        </div>
        <div v-if="rolesLoading" class="rights-page__state" role="status">{{ $t('userRightsPage.loadingRoles') }}</div>
        <div v-else-if="roleCollectionError?.status === 403" class="rights-page__state">{{ $t('userRightsPage.noRoleAccess') }}</div>
        <div v-else-if="roleCollectionError" class="rights-page__state">
          {{ $t('userRightsPage.loadFailed') }}
          <button type="button" class="rights-page__retry" @click="loadTeam(activeTeamId)">{{ $t('userRightsPage.retry') }}</button>
        </div>
        <div v-else-if="!roles.length" class="rights-page__state">{{ $t('userRightsPage.noRoles') }}</div>
        <template v-else>
          <label class="rights-page__mobile-label" for="rights-role">{{ $t('addRoleModal.chooseRole') }}</label>
          <select id="rights-role" class="rights-page__mobile-select input input--default" :value="selectedRoleId || ''" :disabled="saving" @change="onRoleChange">
            <option v-for="role in roles" :key="role.id" :value="role.id">{{ role.title }}</option>
          </select>
          <nav class="rights-page__role-list" :aria-label="$t('userRightsPage.roles')">
            <button v-for="role in roles" :key="role.id" type="button" class="rights-page__role"
              :class="{ 'rights-page__role--selected': selectedRoleId === role.id }"
              :aria-current="selectedRoleId === role.id ? 'true' : undefined" :disabled="saving" @click="selectRole(role)">
              <span>{{ role.title }}</span><span class="rights-page__muted">{{ (role.permissions || []).length }}</span>
            </button>
          </nav>
        </template>
      </aside>

      <main class="rights-page__editor box box--default" aria-labelledby="rights-editor-heading">
        <div v-if="rolesLoading" class="rights-page__state" role="status">{{ $t('userRightsPage.loadingRoles') }}</div>
        <div v-else-if="!selectedRole" class="rights-page__state">{{ $t('userRightsPage.chooseRole') }}</div>
        <template v-else>
          <div class="rights-page__editor-heading">
            <div>
              <p class="rights-page__muted">{{ $t('userRightsPage.editingRole') }}</p>
              <h2 id="rights-editor-heading">{{ selectedRole.title }}</h2>
              <p v-if="can('permission_access') && !permissionsLoading && !permissionsError" class="rights-page__muted">
                {{ $t('userRightsPage.enabledCount', { enabled: enabledCount, total: displayedPermissions.length }) }}
              </p>
            </div>
            <button v-if="can('role_delete')" type="button" class="rights-page__delete" :disabled="saving" @click="deleteRoleModal(selectedRole.id)">{{ $t('userRightsPage.deleteRole') }}</button>
          </div>
          <div v-if="!can('permission_access')" class="rights-page__state">{{ $t('userRightsPage.noPermissionAccess') }}</div>
          <div v-else-if="permissionsLoading" class="rights-page__state" role="status">{{ $t('userRightsPage.loadingPermissions') }}</div>
          <div v-else-if="permissionsError === 403" class="rights-page__state">{{ $t('userRightsPage.noPermissionAccess') }}</div>
          <div v-else-if="permissionsError" class="rights-page__state">
            {{ $t('userRightsPage.loadFailed') }}
            <button type="button" class="rights-page__retry" @click="getAllPermissions()">{{ $t('userRightsPage.retry') }}</button>
          </div>
          <div v-else-if="!permissionsData.length" class="rights-page__state">{{ $t('userRightsPage.noPermissions') }}</div>
          <div v-else-if="!displayedPermissions.length" class="rights-page__state">{{ $t('userRightsPage.noVisiblePermissions') }}</div>
          <template v-else>
            <p v-if="!can('permission_edit')" class="rights-page__readonly">{{ $t('userRightsPage.readOnly') }}</p>
            <div class="rights-page__tools">
              <div class="rights-page__search">
                <label for="rights-search">{{ $t('userRightsPage.searchLabel') }}</label>
                <input id="rights-search" v-model.trim="searchText" class="input input--default" type="search" :placeholder="$t('userRightsPage.searchPlaceholder')" />
              </div>
              <fieldset class="rights-page__filters">
                <legend>{{ $t('userRightsPage.filterLabel') }}</legend>
                <label v-for="option in filterOptions" :key="option.value" :class="{ 'rights-page__filter--active': statusFilter === option.value }">
                  <input v-model="statusFilter" type="radio" name="rights-status" :value="option.value" />{{ $t(option.label) }}
                </label>
              </fieldset>
            </div>
            <div v-if="!visibleGroups.length" class="rights-page__state">{{ $t('userRightsPage.noMatches') }}</div>
            <div v-else class="rights-page__groups">
              <section v-for="group in visibleGroups" :key="group.key" class="rights-page__group" :aria-labelledby="'rights-group-' + group.key">
                <div class="rights-page__group-heading">
                  <div class="rights-page__group-title">
                    <h3 :id="'rights-group-' + group.key">{{ $t('userRightsPage.groups.' + group.key) }}</h3>
                    <span class="rights-page__muted">{{ $t('userRightsPage.groupCount', { enabled: group.enabled, total: group.total }) }}</span>
                  </div>
                  <span class="checkbox rights-page__checkbox rights-page__group-toggle">
                    <input type="checkbox" class="checkbox__checkbox"
                      :checked="group.enabled === group.total"
                      :indeterminate.prop="group.enabled > 0 && group.enabled < group.total"
                      :disabled="!can('permission_edit') || saving"
                      :aria-label="$t(group.enabled === group.total ? 'userRightsPage.deselectGroup' : 'userRightsPage.selectGroup', { group: $t('userRightsPage.groups.' + group.key) })"
                      :title="$t(group.enabled === group.total ? 'userRightsPage.deselectGroup' : 'userRightsPage.selectGroup', { group: $t('userRightsPage.groups.' + group.key) })"
                      @change="toggleGroup(group, $event.target.checked)" />
                    <span class="checkbox__label" aria-hidden="true"></span>
                  </span>
                </div>
                <label v-for="permission in group.items" :key="permission.id" class="rights-page__permission" :class="{ 'rights-page__permission--readonly': !can('permission_edit') }">
                  <span>{{ permission.title }}</span>
                  <span class="checkbox rights-page__checkbox">
                    <input type="checkbox" class="checkbox__checkbox" :checked="permissionIds.includes(permission.id)" :disabled="!can('permission_edit') || saving"
                      @change="togglePermission(permission.id, $event.target.checked)" />
                    <span class="checkbox__label" aria-hidden="true"></span>
                  </span>
                </label>
              </section>
            </div>
            <div v-if="isDirty && can('permission_edit')" class="rights-page__save-bar" role="status">
              <span>{{ $t('userRightsPage.unsavedChanges') }}</span>
              <div class="rights-page__save-actions">
                <button type="button" class="rights-page__secondary button button--cancel" :disabled="saving" @click="resetPermissions">{{ $t('buttons.cancelBtn') }}</button>
                <button type="button" class="button button--default" :disabled="saving" @click="applyPermissions">{{ saving ? $t('userRightsPage.saving') : $t('buttons.saveBtn') }}</button>
              </div>
            </div>
          </template>
        </template>
      </main>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const store = useStore();
const route = useRoute();
const router = useRouter();
const { t } = useI18n();
const teamsLoading = ref(false);
const rolesLoading = ref(false);
const permissionsLoading = ref(false);
const permissionsError = ref(false);
const saving = ref(false);
const activeTeamId = ref(null);
const selectedRoleId = ref(null);
const roleSnapshot = ref(null);
const permissionIds = ref([]);
const savedPermissionIds = ref([]);
const searchText = ref('');
const statusFilter = ref('all');
let teamLoadVersion = 0;

const roles = computed(() => store.getters.roles || []);
const teams = computed(() => store.getters.teams || []);
const isSuperAdmin = computed(() => store.getters.isSuperAdmin);
const permissionsData = computed(() => store.getters.permissions || []);
const roleCollectionError = computed(() => store.getters.roleCollectionError);
const selectedRole = computed(() => roles.value.find(role => role.id === selectedRoleId.value) || roleSnapshot.value);
const roleTeams = computed(() => {
  const authorizedIds = store.getters.authorizedTeamIds('role_access');
  if (authorizedIds === null) return isSuperAdmin.value ? teams.value : [];
  const allowed = new Set(Array.isArray(authorizedIds) ? authorizedIds : []);
  return store.getters.authorizationMemberships
    .filter(membership => membership?.team?.id != null && allowed.has(membership.team.id))
    .map(membership => membership.team);
});
const can = ability => isSuperAdmin.value || (activeTeamId.value !== null && store.getters.hasTeamPermission(ability, activeTeamId.value));
const isDirty = computed(() => permissionIds.value.length !== savedPermissionIds.value.length ||
  permissionIds.value.some(id => !savedPermissionIds.value.includes(id)));
const filterOptions = [
  { value: 'all', label: 'userRightsPage.all' },
  { value: 'enabled', label: 'userRightsPage.enabled' },
  { value: 'disabled', label: 'userRightsPage.disabled' },
];
const groupPrefixes = [
  ['customerPolicyOffers', ['customer_policy_offer']],
  ['potentialPolicies', ['potential_policy']],
  ['salesCategoryPrices', ['sales_category_price']],
  ['salesCategories', ['sales_category']],
  ['branchCategoryServices', ['branch_category_service']],
  ['branchCategories', ['branch_category']],
  ['policyServicePayments', ['policy_service_payment']],
  ['policyServices', ['policy_service']],
  ['policyAccidents', ['policy_accident']],
  ['policyInvoices', ['policy_invoice']],
  ['policyOffers', ['policy_offer']],
  ['policies', ['policy', 'policies']],
  ['customerAddresses', ['customer_address']],
  ['customerPhones', ['customer_phone']],
  ['customerEmails', ['customer_email']],
  ['customers', ['customer']],
  ['userBanks', ['user_bank']],
  ['userAddresses', ['user_address']],
  ['users', ['user', 'users', 'view_all_users', 'profile_password']],
  ['roles', ['role']],
  ['teams', ['team']],
  ['reports', ['report']],
  ['transactions', ['transaction']],
  ['taxes', ['tax']],
  ['leads', ['lead']],
  ['pendencies', ['pendency']],
  ['calls', ['call']],
  ['notes', ['note']],
  ['permissions', ['permission']],
  ['auditLogs', ['audit_log']],
  ['branches', ['branch']],
  ['companies', ['company']],
  ['serviceCategories', ['service_category']],
  ['services', ['service']],
  ['banks', ['bank']],
  ['settings', ['settings']],
  ['titles', ['title']],
  ['genders', ['gender']],
  ['countries', ['country']],
  ['terminationServices', ['termination_service']],
  ['terminations', ['termination']],
  ['dashboards', ['sales_dashboard', 'internal_dashboard']],
  ['provisions', ['provision_import']],
  ['mandates', ['mandate']],
];
const groupOrder = [
  'customers', 'policies', 'users', 'pendencies',
  'provisions', 'customerAddresses', 'customerPhones',
  'customerEmails', 'leads', 'customerPolicyOffers', 'policyServices',
  'policyServicePayments', 'policyAccidents', 'policyInvoices',
  'policyOffers', 'potentialPolicies', 'salesCategories',
  'salesCategoryPrices', 'userBanks', 'userAddresses',
  'roles', 'teams', 'permissions', 'auditLogs', 'branches',
  'branchCategories', 'branchCategoryServices', 'companies',
  'services', 'serviceCategories', 'banks', 'reports',
  'transactions', 'taxes', 'calls', 'notes',
  'terminations', 'terminationServices', 'mandates', 'dashboards', 'settings', 'titles', 'genders', 'countries', 'other',
];
// Add a key from groupOrder here to hide another category from this page.
// Hidden permissions remain in permissionIds and are preserved when a role is saved.
const hiddenGroupKeys = new Set(['titles', 'genders', 'countries']);

const groupKeyFor = permission => {
  const name = String(permission.name || permission.title || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  const match = groupPrefixes.find(([, prefixes]) => prefixes.some(prefix => name === prefix || name.startsWith(prefix + '_')));
  return match?.[0] || 'other';
};
const displayedPermissions = computed(() =>
  permissionsData.value.filter(permission => !hiddenGroupKeys.has(groupKeyFor(permission)))
);
const enabledCount = computed(() =>
  displayedPermissions.value.filter(permission => permissionIds.value.includes(permission.id)).length
);
const visibleGroups = computed(() => {
  const groups = new Map();
  const query = searchText.value.toLocaleLowerCase();
  for (const permission of displayedPermissions.value) {
    const enabled = permissionIds.value.includes(permission.id);
    const key = groupKeyFor(permission);
    if (!groups.has(key)) groups.set(key, { key, items: [], ids: [], enabled: 0, total: 0 });
    const group = groups.get(key);
    group.ids.push(permission.id);
    group.total += 1;
    if (enabled) group.enabled += 1;
    if (query && !String(permission.title || '').toLocaleLowerCase().includes(query)) continue;
    if (statusFilter.value === 'enabled' && !enabled) continue;
    if (statusFilter.value === 'disabled' && enabled) continue;
    group.items.push(permission);
  }
  return [...groups.values()]
    .filter(group => group.items.length)
    .sort((a, b) => groupOrder.indexOf(a.key) - groupOrder.indexOf(b.key));
});

const confirmDiscard = () => !saving.value && (!isDirty.value || window.confirm(t('userRightsPage.discardConfirm')));
const beforeUnload = event => {
  if (!isDirty.value && !saving.value) return;
  event.preventDefault();
  event.returnValue = '';
};
const setDraftFromRole = role => {
  roleSnapshot.value = role;
  selectedRoleId.value = role?.id ?? null;
  store.commit('setSelectedRole', role || {});
  permissionIds.value = (role?.permissions || []).map(permission => permission.id);
  savedPermissionIds.value = [...permissionIds.value];
};
const selectRole = role => {
  if (!role || role.id === selectedRoleId.value || saving.value) return;
  if (!confirmDiscard()) return;
  setDraftFromRole(role);
};
const onRoleChange = event => {
  selectRole(roles.value.find(role => role.id === Number(event.target.value)));
  event.target.value = selectedRoleId.value || '';
};
const togglePermission = (id, checked) => {
  permissionIds.value = checked
    ? permissionIds.value.includes(id) ? permissionIds.value : [...permissionIds.value, id]
    : permissionIds.value.filter(value => value !== id);
};
const toggleGroup = (group, checked) => {
  if (!can('permission_edit') || saving.value) return;
  const groupIds = new Set(group.ids);
  permissionIds.value = checked
    ? [...new Set([...permissionIds.value, ...group.ids])]
    : permissionIds.value.filter(id => !groupIds.has(id));
};
const resetPermissions = () => { permissionIds.value = [...savedPermissionIds.value]; };
const getAllPermissions = async (teamId = activeTeamId.value) => {
  permissionsLoading.value = true;
  permissionsError.value = false;
  try { await store.dispatch('getPermissions', teamId); }
  catch (error) { permissionsError.value = error.response?.status || true; }
  finally { permissionsLoading.value = false; }
};
const loadTeam = async (teamId, updateRoute = true) => {
  const version = ++teamLoadVersion;
  activeTeamId.value = teamId;
  setDraftFromRole(null);
  searchText.value = '';
  statusFilter.value = 'all';
  store.commit('setPermissions', []);
  permissionsError.value = false;
  rolesLoading.value = true;
  try {
    if (updateRoute && String(route.query.team_id || '') !== String(teamId)) {
      await router.replace({ query: { ...route.query, team_id: teamId } });
    }
    await store.dispatch('getRolesByTeam', teamId);
    if (version !== teamLoadVersion) return;
    if (roles.value.length) setDraftFromRole(roles.value[0]);
    if (can('permission_access')) await getAllPermissions(teamId);
  } finally {
    if (version === teamLoadVersion) rolesLoading.value = false;
  }
};
const onTeamChange = async event => {
  const nextId = Number(event.target.value);
  if (nextId === activeTeamId.value) return;
  if (!confirmDiscard()) {
    event.target.value = activeTeamId.value || '';
    return;
  }
  await loadTeam(nextId);
};
const deleteRoleModal = id => {
  store.commit('setDeleteModal', {
    display: true,
    message: selectedRole.value?.title || '',
    action: () => deleteRole(id),
    id,
  });
};
const deleteRole = async id => {
  if (!confirmDiscard()) return;
  try {
    await store.dispatch('deleteRole', { id, teamId: activeTeamId.value });
    await loadTeam(activeTeamId.value);
  } catch (error) { console.error(error); }
};
const applyPermissions = async () => {
  if (!isDirty.value || saving.value || !can('permission_edit') || !selectedRoleId.value) return;
  saving.value = true;
  const roleId = selectedRoleId.value;
  const submittedIds = [...permissionIds.value];
  try {
    const response = await store.dispatch('updateRole', { id: roleId, permissions: submittedIds, teamId: activeTeamId.value });
    savedPermissionIds.value = submittedIds;
    store.commit('setToast', { display: true, type: 'success', message: t('userRightsPage.saveSuccess') });
    const updatedRole = response?.data?.data;
    if (updatedRole) {
      roleSnapshot.value = updatedRole;
      store.commit('setSelectedRole', updatedRole);
    }
    await store.dispatch('getRolesByTeam', activeTeamId.value);
    const refreshedRole = roles.value.find(role => role.id === roleId);
    if (refreshedRole) {
      roleSnapshot.value = refreshedRole;
      store.commit('setSelectedRole', refreshedRole);
    }
    await store.dispatch('updateLoggedUser', store.getters.loggedUser.id);
  } catch (error) { console.error(error); }
  finally { saving.value = false; }
};

watch(roles, nextRoles => {
  if (rolesLoading.value || saving.value || !nextRoles.length) return;
  const current = nextRoles.find(role => role.id === selectedRoleId.value);
  if (current) {
    roleSnapshot.value = current;
    store.commit('setSelectedRole', current);
    if (!isDirty.value) setDraftFromRole(current);
  } else if (!isDirty.value) setDraftFromRole(nextRoles[0]);
});
watch(roleTeams, nextTeams => {
  if (nextTeams.length && !activeTeamId.value && !teamsLoading.value && teamLoadVersion === 0) {
    loadTeam(nextTeams[0].id);
  }
});
onBeforeRouteLeave(() => confirmDiscard());
onBeforeRouteUpdate(to => {
  const nextId = Number(to.query.team_id);
  if (nextId === activeTeamId.value || !Number.isInteger(nextId)) return true;
  if (!confirmDiscard()) return false;
  const team = roleTeams.value.find(item => item.id === nextId);
  if (team) loadTeam(team.id, false);
  return true;
});
onMounted(async () => {
  window.addEventListener('beforeunload', beforeUnload);
  store.commit('setRoles', []);
  store.commit('setPermissions', []);
  if (isSuperAdmin.value && !teams.value.length) {
    teamsLoading.value = true;
    try { await store.dispatch('getTeams'); }
    finally { teamsLoading.value = false; }
  }
  const requestedId = Number(route.query.team_id);
  const initialTeam = roleTeams.value.find(team => team.id === requestedId)
    || roleTeams.value.find(team => team.id === store.getters.currentTeamId)
    || roleTeams.value[0];
  if (initialTeam) await loadTeam(initialTeam.id);
});
onBeforeUnmount(() => window.removeEventListener('beforeunload', beforeUnload));
</script>

<style scoped lang="scss">
.rights-page {
  color: var(--text-9);
  padding-bottom: 24px;
  h1, h2, h3, p { margin: 0; }
  h1 { font-size: var(--large); }
  h2 { font-size: var(--medium); font-family: var(--muli-text-semi-bold); }
  h3 { font-size: var(--small); font-family: var(--muli-text-semi-bold); }
  button { cursor: pointer; }
  button:disabled { cursor: wait; opacity: .6; }
  button:focus-visible, input:focus-visible, select:focus-visible { outline: 3px solid var(--border-2); outline-offset: 3px; }
  &__header, &__actions, &__team, &__heading, &__editor-heading, &__group-heading, &__permission, &__save-bar, &__save-actions {
    display: flex; align-items: center; gap: 12px;
  }
  &__header { justify-content: space-between; flex-wrap: wrap; gap: 20px; margin-bottom: 20px; }
  &__header p, &__muted { color: var(--text-2); font-size: var(--x-small); }
  &__header p { margin-top: 5px; }
  &__actions { flex-wrap: wrap; }
  &__actions .button, &__save-actions .button { min-width: 130px; padding: 0 18px; }
  &__secondary { padding: 0 18px; }
  &__delete { color: var(--text-12); background: var(--bg-1); border: 1px solid var(--border-9); border-radius: var(--radius-1); padding: 11px 15px; flex: 0 0 auto; font-size: var(--x-small); font-family: var(--muli-text-semi-bold); }
  &__delete:hover { background: var(--highlight-2); }
  &__team { background: var(--bg-1); padding: 16px 20px; margin-bottom: 20px; flex-wrap: wrap; }
  &__team label { color: var(--text-2); font-size: var(--x-small); font-family: var(--muli-text-semi-bold); }
  &__team select, &__mobile-select, &__search input { background-color: var(--bg-3); color: var(--text-3); border: 1px solid transparent; height: 40px; padding: 0 10px; font-size: var(--x-small); font-family: var(--muli-text-semi-bold); }
  &__team select, &__mobile-select { color-scheme: light; }
  &__team select option, &__mobile-select option { background-color: var(--bg-1); color: var(--text-9); }
  &__team select { min-width: min(100%, 260px); }
  &__layout { display: grid; grid-template-columns: minmax(220px, 260px) minmax(0, 1fr); gap: 20px; align-items: start; }
  &__roles, &__editor { background: var(--bg-1); min-width: 0; padding: 20px; }
  &__editor { container-type: inline-size; }
  &__heading { justify-content: space-between; padding-bottom: 15px; border-bottom: 1px solid var(--border-10); }
  &__role-list { display: grid; gap: 6px; margin-top: 12px; }
  &__role { display: flex; justify-content: space-between; align-items: center; gap: 12px; width: 100%; padding: 12px; text-align: left; background: transparent; color: var(--text-9); border: 1px solid transparent; border-radius: var(--radius-1); font-size: var(--small); font-family: var(--muli-text-regular); }
  &__role:hover { background: var(--bg-17); }
  &__role--selected { background: var(--highlight-1); border-color: var(--border-11); font-family: var(--muli-text-semi-bold); }
  &__mobile-label, &__mobile-select { display: none; }
  &__mobile-label { color: var(--text-2); font-size: var(--x-small); font-family: var(--muli-text-semi-bold); }
  &__editor-heading { justify-content: space-between; align-items: flex-start; padding-bottom: 20px; border-bottom: 1px solid var(--border-10); }
  &__editor-heading h2 { margin: 5px 0; }
  &__state { padding: 30px 20px; text-align: center; color: var(--text-2); font-size: var(--small); }
  &__retry { display: block; margin: 10px auto 0; padding: 5px; color: var(--text-10); background: transparent; text-decoration: underline; border: 0; }
  &__readonly { margin-top: 18px !important; padding: 12px; color: var(--text-2); background: var(--bg-17); border-radius: var(--radius-1); }
  &__tools { display: grid; grid-template-columns: minmax(180px, 1fr) auto; gap: 16px; align-items: end; padding: 20px 0; }
  &__search { display: grid; gap: 6px; }
  &__search label, &__filters legend { color: var(--text-2); font-size: var(--x-small); font-family: var(--muli-text-semi-bold); }
  &__search input { width: 100%; }
  &__filters { display: flex; flex-wrap: wrap; gap: 4px; margin: 0; padding: 0; border: 0; }
  &__filters legend { margin-bottom: 5px; }
  &__filters label { position: relative; padding: 10px 12px; color: var(--text-2); border: 1px solid var(--border-10); border-radius: var(--radius-1); cursor: pointer; font-size: var(--x-small); font-family: var(--muli-text-semi-bold); }
  &__filters input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
  &__filters label:focus-within { outline: 3px solid var(--border-2); outline-offset: 2px; }
  &__filter--active { color: var(--text-9) !important; border-color: var(--border-2) !important; background: var(--highlight-1); }
  &__groups { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; align-items: start; }
  &__group { border: 1px solid var(--border-10); border-radius: var(--radius-1); overflow: hidden; }
  &__group-heading { justify-content: space-between; background: var(--bg-17); padding: 13px 16px; }
  &__group-title { display: flex; flex-direction: column; align-items: flex-start; gap: 4px; min-width: 0; }
  &__group-heading h3 { overflow-wrap: anywhere; }
  &__permission { justify-content: space-between; min-height: 50px; padding: 10px 16px; cursor: pointer; border-top: 1px solid var(--border-10); font-size: var(--x-small); }
  &__permission:hover { background: var(--bg-17); }
  &__permission span { min-width: 0; overflow-wrap: anywhere; }
  &__checkbox { position: relative; display: inline-flex; flex: 0 0 18px; width: 18px; height: 18px; }
  &__checkbox .checkbox__checkbox { inset: 0; width: 18px; height: 18px; margin: 0; opacity: 0; cursor: pointer; z-index: 1; }
  &__checkbox .checkbox__checkbox:focus-visible ~ .checkbox__label { outline: 3px solid var(--border-2); outline-offset: 3px; }
  &__checkbox .checkbox__label { flex: 0 0 18px; }
  &__group-toggle { flex: 0 0 30px; width: 30px; height: 30px; margin-right: -6px; align-items: center; justify-content: center; }
  &__group-toggle .checkbox__checkbox { width: 30px; height: 30px; }
  &__group-toggle .checkbox__checkbox:disabled, &__group-toggle .checkbox__checkbox:disabled ~ .checkbox__label { cursor: default; opacity: .6; }
  &__group-toggle .checkbox__checkbox:indeterminate ~ .checkbox__label { background: var(--bg-2); }
  &__group-toggle .checkbox__checkbox:indeterminate ~ .checkbox__label::after { content: ''; display: block; width: 10px; height: 2px; margin: 7px auto; background: var(--text-1); }
  &__permission--readonly .checkbox__label, &__permission--readonly .checkbox__checkbox { cursor: default; }
  &__permission--readonly, &__permission--readonly input { cursor: default; }
  &__save-bar { position: sticky; bottom: 0; justify-content: space-between; flex-wrap: wrap; padding: 15px; margin-top: 20px; background: var(--bg-1); border: 1px solid var(--border-11); border-radius: var(--radius-1); box-shadow: 0 4px 16px var(--shadow-4); z-index: 1; font-family: var(--muli-text-semi-bold); }
}
@container (max-width: 599px) {
  .rights-page__groups { grid-template-columns: repeat(2, minmax(0, 1fr)); }
}
@container (max-width: 419px) {
  .rights-page__groups { grid-template-columns: 1fr; }
}
@media (max-width: 900px) {
  .rights-page {
    &__layout { grid-template-columns: 1fr; gap: 14px; }
    &__roles, &__editor { padding: 16px; }
    &__role-list { display: none; }
    &__mobile-label, &__mobile-select { display: block; }
    &__mobile-label { margin: 14px 0 6px; }
    &__mobile-select { width: 100%; }
  }
}
@media (max-width: 700px) {
  .rights-page__groups { grid-template-columns: 1fr; }
}
@media (max-width: 600px) {
  .rights-page {
    &__header, &__actions { align-items: stretch; flex-direction: column; }
    &__actions, &__actions button { width: 100%; }
    &__team select { width: 100%; }
    &__editor-heading { flex-wrap: wrap; }
    &__tools { display: block; }
    &__search { margin-bottom: 16px; }
    &__save-bar, &__save-actions { width: 100%; }
    &__save-actions button { flex: 1; }
  }
}
[data-theme='dark'] .rights-page__team select,
[data-theme='dark'] .rights-page__mobile-select { color-scheme: dark; }
[data-theme='dark'] .rights-page__delete { color: var(--bg-26); }
</style>
